import React, { useState, useEffect } from 'react';

import {
  AIRCRAFT_TYPE_OPTIONS_W_ALL,
  DEFAULT_OPTION,
  SELECT_OPTION_ALL,
} from 'constants';
import { LRUFactory, ReportFactory } from 'classes';

import { Row, Col, Button } from 'react-bootstrap';
import Select from 'components/inputs/Select';
import ReportSummaryOverlay from 'components/overlays/ReportSummaryOverlay';

import styles from './styles.module.css';

const idPrefix = 'LRUDetailReport';
const LRUDetail = (props) => {
  const { aircraftMap, aircraftList, LruId, fleetFilter, onFleetFilterChange } =
    props;

  const [shipOption, setShipOption] = useState(SELECT_OPTION_ALL.value);
  const [LRUOption, setLRUOption] = useState(SELECT_OPTION_ALL.value);
  const [positionOption, setPositionOption] = useState(SELECT_OPTION_ALL.value);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [data, setData] = useState(null);
  const [messageOverlayOpen, setMessageOverlayOpen] = useState(false);
  const [selectedAircraftId, setSelectedAircraftId] = useState(
    SELECT_OPTION_ALL.value,
  );

  useEffect(() => {
    const getData = async () => {
      const data = await LRUFactory.getLruById(LruId);
      setData(data);
    };
    if (LruId && !LRUOption) {
      getData();
      if (data) {
        const aircraft = aircraftMap.get(data.aircraft_id);
        setShipOption(aircraft ? aircraft.tail : SELECT_OPTION_ALL.value);
        setSelectedAircraftId(aircraft ? aircraft.id : SELECT_OPTION_ALL.value);
        setLRUOption(data ? data.lru_type?.value : SELECT_OPTION_ALL.value);
        setPositionOption(data ? data.position : SELECT_OPTION_ALL.value);
        setOverlayOpen(true);
      }
    }
  }, [LruId, LRUOption, aircraftMap, data]);

  const ship = [DEFAULT_OPTION]
    .concat(
      !aircraftList
        ? []
        : (!fleetFilter
            ? aircraftList
            : aircraftList.filter(
                (aircraft) => aircraft?.aircraft_type?.id === fleetFilter,
              )
          ).map((aircraft) => {
            return {
              value: aircraft.id,
              name: aircraft.tail,
            };
          }),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const selectedAircraft = !selectedAircraftId
    ? null
    : aircraftMap.get(selectedAircraftId);

  const aircraftLopa = selectedAircraft ? selectedAircraft.lopa : null;

  const lopaMap = {};
  aircraftLopa &&
    Object.keys(aircraftLopa).forEach((type) => {
      if (type === 'id' || type === 'classes') {
        return;
      }
      Object.values(aircraftLopa[type]).forEach((value) => {
        if (!lopaMap[value.lru_type.value]) {
          lopaMap[value.lru_type.value] = [];
        }
        lopaMap[value.lru_type.value].push(value.id);
      });
    });

  const LRUs = [DEFAULT_OPTION].concat(
    Object.keys(lopaMap).map((type) => {
      return {
        value: type,
        name: type,
      };
    }),
  );

  const aircraftType = selectedAircraft?.aircraft_type;

  const position =
    aircraftType && LRUOption
      ? [DEFAULT_OPTION].concat(
          lopaMap[LRUOption].map((p) => {
            return {
              value: p,
              name: p,
            };
          }),
        )
      : [DEFAULT_OPTION];

  return (
    <>
      <Row className={styles.root} noGutters>
        <Col>
          <Row className={styles.title} noGutters>
            <Col data-cy-id={`${idPrefix}-ScreenHeader`} className="title_1">
              {' '}
              LRU detail report{' '}
            </Col>
            <Col md="auto" className={styles.note}>
              Note: Data is available for previous 30 days.
            </Col>
          </Row>
          <Row
            className={styles.filterRow}
            data-cy-id={`${idPrefix}-Filters`}
            noGutters
          >
            <Col>
              <Row>
                <Col md="auto" className="mr-5">
                  <Select
                    idPrefix={`${idPrefix}-Fleet`}
                    data-cy-id={`${idPrefix}-FiltersPanel-FleetSelect`}
                    label="Fleet: "
                    options={AIRCRAFT_TYPE_OPTIONS_W_ALL}
                    value={fleetFilter}
                    onChange={(e) => {
                      const {
                        target: { value },
                      } = e;
                      onFleetFilterChange(value);
                      setShipOption(SELECT_OPTION_ALL.value);
                      setSelectedAircraftId(SELECT_OPTION_ALL.value);
                      setLRUOption(SELECT_OPTION_ALL.value);
                      setPositionOption(SELECT_OPTION_ALL.value);
                    }}
                  />
                </Col>
                <Col md="auto">
                  <Select
                    idPrefix={`${idPrefix}-Ship`}
                    data-cy-id={`${idPrefix}-FiltersPanel-TailSelect`}
                    label="Tail: "
                    options={ship}
                    value={selectedAircraftId}
                    onChange={(e) => {
                      const {
                        target: { value },
                      } = e;
                      const selectedAircraft = aircraftMap.get(value);
                      setShipOption(selectedAircraft.tail);
                      setSelectedAircraftId(value);
                      setLRUOption(SELECT_OPTION_ALL.value);
                      setPositionOption(SELECT_OPTION_ALL.value);
                    }}
                  />
                </Col>
                <Col md="auto">
                  <Select
                    idPrefix={`${idPrefix}-Departure-utc`}
                    data-cy-id={`${idPrefix}-FiltersPanel-LruSelect`}
                    label="LRU: "
                    options={LRUs}
                    value={LRUOption}
                    onChange={(e) => {
                      const {
                        target: { value },
                      } = e;
                      setLRUOption(value);
                      setPositionOption(SELECT_OPTION_ALL.value);
                    }}
                    disabled={LRUs.length <= 1}
                  />
                </Col>
                <Col md="auto">
                  <Select
                    idPrefix={`${idPrefix}-Flight`}
                    data-cy-id={`${idPrefix}-FiltersPanel-PositionSelect`}
                    label="Position: "
                    options={position}
                    value={positionOption}
                    onChange={(e) => {
                      const {
                        target: { value },
                      } = e;
                      setPositionOption(value);
                    }}
                    disabled={position.length <= 1}
                  />
                </Col>
              </Row>
            </Col>

            <Col md="auto">
              <Button
                className="button_foc"
                data-cy-id={`${idPrefix}-CreateReportButton`}
                disabled={!positionOption}
                onClick={() => setOverlayOpen(true)}
              >
                Create report
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <ReportSummaryOverlay
        title="LRU detail report"
        data={
          <>
            <Row>
              <Col> Fleet </Col>
              <Col>
                {fleetFilter === SELECT_OPTION_ALL.value
                  ? SELECT_OPTION_ALL.name
                  : AIRCRAFT_TYPE_OPTIONS_W_ALL[fleetFilter]?.name}
              </Col>
            </Row>
            <Row>
              <Col> Tail </Col>
              <Col> {shipOption} </Col>
            </Row>
            <Row>
              <Col> LRU </Col>
              <Col>
                {' '}
                {LRUOption === SELECT_OPTION_ALL.value ? null : LRUOption}{' '}
              </Col>
            </Row>
            <Row>
              <Col> Position </Col>
              <Col> {positionOption} </Col>
            </Row>
          </>
        }
        isOpen={overlayOpen}
        loading={messageOverlayOpen}
        onCancel={() => setOverlayOpen(false)}
        onDone={async () => {
          setMessageOverlayOpen(true);
          await ReportFactory.getLruDetailReport(
            selectedAircraftId,
            LRUOption,
            positionOption,
          );
          setMessageOverlayOpen(false);
          setOverlayOpen(false);
        }}
      />
    </>
  );
};

export default LRUDetail;
